/** Signature of the public AV link (#222): `?exp=…&sig=…` and the SDK headers must never reach Sentry. */
export const FILTERED = "[Filtered]";

const SIGNED_QUERY_PARAM = /(^|[?&])(exp|sig)=[^&#\s"']*/gi;
const HAS_SIGNED_QUERY_PARAM = /(^|[?&])(exp|sig)=/i;
const SIGNED_PARAM_NAME = /^(exp|sig)$/i;
const SIGNED_HEADER_NAME = /^x-av-erfassung-(exp|sig)$/i;

/** Replaces the values of the `exp` and `sig` query params in a URL, query string or free text. */
export function scrubSignedLinkParams(text: string): string {
  return text.includes("=") ? text.replace(SIGNED_QUERY_PARAM, `$1$2=${FILTERED}`) : text;
}

/** True when a URL or `location.search` carries the link signature. */
export function hasSignedLinkParams(url: string): boolean {
  return HAS_SIGNED_QUERY_PARAM.test(url);
}

function isPlainContainer(value: unknown): value is Record<string, unknown> | unknown[] {
  if (Array.isArray(value)) return true;
  if (typeof value !== "object" || value === null) return false;
  const proto = Object.getPrototypeOf(value);
  return proto === Object.prototype || proto === null;
}

function scrubQueryString(value: unknown): unknown {
  if (Array.isArray(value)) {
    return value.map((pair) =>
      Array.isArray(pair) && typeof pair[0] === "string" && SIGNED_PARAM_NAME.test(pair[0]) ? [pair[0], FILTERED] : pair,
    );
  }
  if (isPlainContainer(value)) {
    return Object.fromEntries(
      Object.entries(value).map(([k, v]) => [k, SIGNED_PARAM_NAME.test(k) ? FILTERED : v]),
    );
  }
  return value;
}

/** Scrubs a Sentry event or breadcrumb in place (every string, signed headers, `query_string`). */
export function scrubSignedLinks<T>(value: T, seen: WeakSet<object> = new WeakSet()): T {
  if (typeof value === "string") return scrubSignedLinkParams(value) as T;
  if (!isPlainContainer(value) || seen.has(value)) return value;
  seen.add(value);
  if (Array.isArray(value)) {
    value.forEach((item, i) => (value[i] = scrubSignedLinks(item, seen)));
    return value;
  }
  const record: Record<string, unknown> = value;
  for (const [key, item] of Object.entries(record)) {
    record[key] = SIGNED_HEADER_NAME.test(key)
      ? FILTERED
      : scrubSignedLinks(key === "query_string" ? scrubQueryString(item) : item, seen);
  }
  return value;
}
